import { useState, useMemo } from "react";
import { Label } from "./label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./select";

type Period = "AM" | "PM";

interface TimePickerDemoProps {
	date: Date | undefined;
	setDate: (date: Date | undefined) => void;
}

export function TimePicker12Demo({ date, setDate }: TimePickerDemoProps) {
	const [period, setPeriod] = useState<Period>("AM");

	const timeOptions = useMemo(() => {
		const opts: string[] = [];

		for (let h = 12; h <= 12 + 11; h++) {
			const hour = h % 12 === 0 ? 12 : h % 12;
			for (const m of [0, 30]) {
				opts.push(`${hour}:${m.toString().padStart(2, "0")}`);
			}
		}
		return opts;
	}, []);

	const getCurrentTimeValue = () => {
		if (!date) return "";
		const hour = date.getHours();
		const minutes = date.getMinutes();
		const hour12 = hour % 12 || 12;
		const rounded = minutes >= 30 ? 30 : 0;
		return `${hour12}:${rounded.toString().padStart(2, "0")}`;
	};

	const getCurrentPeriod = (): Period => {
		if (!date) return period;
		return date.getHours() >= 12 ? "PM" : "AM";
	};

	const updateDate = (selectedTime: string, selectedPeriod: Period) => {
		const [hStr, mStr] = selectedTime.split(":");
		if (hStr === undefined || mStr === undefined) return;
		const hour = parseInt(hStr);
		const minute = parseInt(mStr);
		const hour24 =
			selectedPeriod === "AM"
				? hour === 12
					? 0
					: hour
				: hour === 12
					? 12
					: hour + 12;

		const newDate = date ? new Date(date) : new Date();
		newDate.setHours(hour24, minute, 0, 0);
		setDate(newDate);
	};

	const handleTimeChange = (value: string) => {
		updateDate(value, period);
	};

	const handlePeriodChange = (value: Period) => {
		setPeriod(value);
		updateDate(getCurrentTimeValue(), value);
	};

	return (
		<div className="flex items-end gap-4">
			<div className="grid gap-1 text-left">
				<Label className="text-xs">Time</Label>
				<Select value={getCurrentTimeValue()} onValueChange={handleTimeChange}>
					<SelectTrigger className="w-[120px]">
						<SelectValue placeholder="Select time" />
					</SelectTrigger>
					<SelectContent>
						{timeOptions.map((time) => (
							<SelectItem key={time} value={time}>
								{time}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className="grid gap-1 text-left">
				<Label className="text-xs">Period</Label>
				<Select value={getCurrentPeriod()} onValueChange={handlePeriodChange}>
					<SelectTrigger className="w-[80px]">
						<SelectValue placeholder="Select period" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="AM">AM</SelectItem>
						<SelectItem value="PM">PM</SelectItem>
					</SelectContent>
				</Select>
			</div>
		</div>
	);
}
